'use strict';

/**
 * @fileoverview The site-link vocabulary (LIFE-SHORTLISTED-001 P10.2): the kinds of destination a
 * tenant's site links to, and the closed set of social platforms a social link may name. purchase-api
 * validates stored links against it, marketing-shell draws one glyph per platform, and the editors
 * offer it as a closed list. It lives here, beside the href classification, because it is part of
 * the same link vocabulary, and because this package is dual-use: the API and the browser read one
 * list.
 *
 * Two platforms joined on 2026-10-10 (DTOWNRETAIL-SITE-001 U6, I-14b): `google` — a Google Business
 * Profile, the review-and-directions listing every brick-and-mortar tenant has per shoppe, drawn
 * with Google's own glyph — and `linkedin`, drawn since LIFE-SHORTLISTED-001 U33 with LinkedIn's own
 * [in] artwork. How each platform is drawn is marketing-shell's `SOCIAL_ICON_POLICY`, which covers
 * every platform here.
 *
 * `isSiteLinkHref` is the ONE rule for where a site link may point, read by the API before it stores
 * a link and by the editors before they send one: a web address, an email, or a page on the site.
 * A phone number is not a site link: a phone belongs to a LOCATION (`locations.phone`, rendered by the
 * site's location pages and blocks — the `contact` block carries only a heading and a description;
 * DTOWNRETAIL-SITE-001 U6 corrected this sentence, which had claimed the block carried one). A bare
 * word, an anchor or a protocol-relative `//host` is never a destination.
 *
 * @module @puffin-core/block-schema/siteLinks
 */

const { HREF_KIND, classifyHref } = require('./hrefClassification');

/** What a site link points at. */
const SITE_LINK_KIND = Object.freeze({
  SOCIAL: 'social',
  NEWSLETTER: 'newsletter',
  SHOP: 'shop',
  OTHER: 'other',
});

/** The platforms a social link may name. */
const SOCIAL_PLATFORM = Object.freeze({
  INSTAGRAM: 'instagram',
  PINTEREST: 'pinterest',
  TIKTOK: 'tiktok',
  YOUTUBE: 'youtube',
  THREADS: 'threads',
  SUBSTACK: 'substack',
  FACEBOOK: 'facebook',
  X: 'x',
  GOOGLE: 'google',
  LINKEDIN: 'linkedin',
});

const PLATFORMS = new Set(Object.values(SOCIAL_PLATFORM));

/**
 * How many links a site keeps: a footer's worth, not a directory. The ONE cap: purchase-api's create
 * refuses the next one, and a tenant seed declaring more is refused by tenant-config.
 */
const SITE_LINK_LIMIT = 50;

/**
 * @param {unknown} value
 * @returns {boolean} whether `value` is one of the platforms, exactly
 */
function isSocialPlatform(value) {
  return PLATFORMS.has(value);
}

/** The destination kinds a site link may have. */
const SITE_LINK_HREF_KINDS = new Set([HREF_KIND.EXTERNAL, HREF_KIND.MAILTO, HREF_KIND.INTERNAL]);

/**
 * @param {unknown} href
 * @returns {boolean} whether `href` is a destination a site link may point at
 */
function isSiteLinkHref(href) {
  return SITE_LINK_HREF_KINDS.has(classifyHref(href));
}

module.exports = {
  SITE_LINK_KIND,
  SITE_LINK_LIMIT,
  SOCIAL_PLATFORM,
  isSiteLinkHref,
  isSocialPlatform,
};
